import { pollWhileVisible, type components } from "@roastery/api-client";
import { useApi } from "@roastery/auth";
import { useQueryClient } from "@tanstack/react-query";

export type OperatorFeedbackSummary = components["schemas"]["OperatorFeedbackSummary"];
export type OperatorFeedbackDetail = components["schemas"]["OperatorFeedbackDetail"];
export type FeedbackStatus = NonNullable<OperatorFeedbackSummary["status"]>;
export type FeedbackType = NonNullable<OperatorFeedbackSummary["type"]>;

export const STATUSES: FeedbackStatus[] = [
  "NEW",
  "ACCEPTED",
  "WAITING_FOR_TENANT",
  "PLANNED",
  "DONE",
  "DECLINED",
  "ALREADY_EXISTS",
];
export const TYPES: FeedbackType[] = ["BUG", "IDEA", "QUESTION"];

export const FEEDBACK_PATH = "/api/v1/platform/feedback";
const STATS_PATH = "/api/v1/platform/feedback/stats";
const DETAIL_PATH = "/api/v1/platform/feedback/{id}";

export interface InboxFilters {
  closed: boolean | undefined;
  type: FeedbackType | "";
  tenantId: string;
  unread: boolean;
  query: string;
  page: number;
}

export function useFeedbackInbox(filters: InboxFilters) {
  const api = useApi();
  return api.query.useQuery("get", FEEDBACK_PATH, {
    params: {
      query: {
        closed: filters.closed,
        type: filters.type || undefined,
        tenantId: filters.tenantId || undefined,
        unread: filters.unread || undefined,
        query: filters.query.trim() || undefined,
        page: filters.page,
        size: 25,
      },
    },
  });
}

/**
 * The first page of open reports — blocking bugs first, then the newest activity (the inbox's own
 * order), which is exactly what the dashboard's "needs attention" card wants from it.
 */
export function useOpenFeedback(enabled: boolean) {
  const api = useApi();
  return api.query.useQuery(
    "get",
    FEEDBACK_PATH,
    { params: { query: { closed: false, page: 0, size: 25 } } },
    { enabled, ...pollWhileVisible(60_000) },
  );
}

/** The four numbers and the badge beside "Reports" in the menu — polled like the leads badge. */
export function useFeedbackStats() {
  const api = useApi();
  return api.query.useQuery("get", STATS_PATH, {}, pollWhileVisible(60_000));
}

export function useOperatorFeedback(id: string) {
  const api = useApi();
  return api.query.useQuery("get", DETAIL_PATH, { params: { path: { id } } });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["get", FEEDBACK_PATH] }),
      queryClient.invalidateQueries({ queryKey: ["get", STATS_PATH] }),
      queryClient.invalidateQueries({ queryKey: ["get", DETAIL_PATH] }),
    ]);
}

export function useReplyToFeedback() {
  const api = useApi();
  const invalidate = useInvalidate();
  return api.query.useMutation("post", "/api/v1/platform/feedback/{id}/reply", { onSuccess: invalidate });
}

export function useNoteOnFeedback() {
  const api = useApi();
  const invalidate = useInvalidate();
  return api.query.useMutation("post", "/api/v1/platform/feedback/{id}/notes", { onSuccess: invalidate });
}

export function useAssignFeedback() {
  const api = useApi();
  const invalidate = useInvalidate();
  return api.query.useMutation("put", "/api/v1/platform/feedback/{id}/assignment", { onSuccess: invalidate });
}

export function useMergeFeedback() {
  const api = useApi();
  const invalidate = useInvalidate();
  return api.query.useMutation("post", "/api/v1/platform/feedback/{id}/merge", { onSuccess: invalidate });
}

export function useRenotifyFeedback() {
  const api = useApi();
  const invalidate = useInvalidate();
  return api.query.useMutation("post", "/api/v1/platform/feedback/{id}/renotify", { onSuccess: invalidate });
}

/** A screenshot, fetched with the token; the server writes the opening into the audit log. */
export function useOperatorAttachment() {
  const api = useApi();
  return async (id: string, fileId: string): Promise<string> => {
    const { data } = await api.fetch.GET("/api/v1/platform/feedback/{id}/attachments/{fileId}", {
      params: { path: { id, fileId } },
      parseAs: "blob",
    });
    if (!data) {
      throw new Error("attachment not found");
    }
    return URL.createObjectURL(data as Blob);
  };
}

/** The filtered inbox as a file, fetched with the token and handed to the browser to save. */
export function useExportFeedback() {
  const api = useApi();
  return async (filters: InboxFilters) => {
    const { data } = await api.fetch.GET("/api/v1/platform/feedback/export", {
      params: {
        query: { closed: filters.closed, type: filters.type || undefined, tenantId: filters.tenantId || undefined },
      },
      parseAs: "blob",
    });
    if (!data) {
      throw new Error("export failed");
    }
    const url = URL.createObjectURL(data as Blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "feedback.csv";
    link.click();
    URL.revokeObjectURL(url);
  };
}
